import React from 'react'
import styled from 'styled-components'
import { BrandMark, ChevronRightIcon } from './NavMenu/navIcons'

interface Props {
  mode: 'desktop' | 'mobile-drawer'
  collapsed: boolean
  brandName?: string
  brandSub?: string
  onToggleCollapse?: () => void
  onCloseDrawer?: () => void
}

const SidebarHeader: React.FC<Props> = ({
  mode,
  collapsed,
  brandName = 'אגרוליין',
  brandSub,
  onToggleCollapse,
  onCloseDrawer,
}) => (
  <Head>
    <Brand>
      <Logo aria-hidden>
        <BrandMark />
      </Logo>
      {!collapsed && (
        <NameStack>
          <N1>{brandName}</N1>
          {brandSub ? <N2>{brandSub}</N2> : null}
        </NameStack>
      )}
    </Brand>
    {mode === 'desktop' && (
      <ToggleBtn
        type='button'
        aria-label={collapsed ? 'הרחב' : 'כווץ'}
        onClick={onToggleCollapse}
        $collapsed={collapsed}
      >
        <ChevronRightIcon />
      </ToggleBtn>
    )}
    {mode === 'mobile-drawer' && (
      <CloseBtn type='button' aria-label='סגור' onClick={onCloseDrawer}>
        <svg
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth={2}
          strokeLinecap='round'
        >
          <path d='M18 6 6 18M6 6l12 12' />
        </svg>
      </CloseBtn>
    )}
  </Head>
)

export default SidebarHeader

const Head = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.line2};
  flex-shrink: 0;
  direction: rtl;
`

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`

const Logo = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.ink};
  display: grid;
  place-items: center;
  flex-shrink: 0;
  position: relative;
  color: ${({ theme }) => theme.colors.accent};

  &::after {
    content: '';
    position: absolute;
    inset: 1px;
    border-radius: 9px;
    background: linear-gradient(135deg, rgba(197, 242, 48, 0.18), transparent 60%);
  }

  svg {
    width: 18px;
    height: 18px;
    position: relative;
    z-index: 1;
  }
`

const NameStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
`

const N1 = styled.span`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.ink};
`

const N2 = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 9.5px;
  color: ${({ theme }) => theme.colors.ink3};
  text-transform: uppercase;
  letter-spacing: 0.1em;
`

const ToggleBtn = styled.button<{ $collapsed: boolean }>`
  position: absolute;
  top: 28px;
  inset-inline-end: -14px;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.line};
  color: ${({ theme }) => theme.colors.ink2};
  display: grid;
  place-items: center;
  cursor: pointer;
  z-index: 5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition:
    background ${({ theme }) => theme.motion.fast},
    color ${({ theme }) => theme.motion.fast},
    border-color ${({ theme }) => theme.motion.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};
    border-color: ${({ theme }) => theme.colors.accent};
  }

  svg {
    width: 14px;
    height: 14px;
    transition: transform 320ms cubic-bezier(0.2, 0.8, 0.2, 1);
    transform: ${({ $collapsed }) => ($collapsed ? 'scaleX(-1)' : 'none')};
  }
`

const CloseBtn = styled.button`
  width: 30px;
  height: 30px;
  border: 1px solid ${({ theme }) => theme.colors.line};
  background: ${({ theme }) => theme.colors.bg};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ink2};
  display: grid;
  place-items: center;
  cursor: pointer;
  flex-shrink: 0;

  svg { width: 14px; height: 14px; }

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    border-color: ${({ theme }) => theme.colors.inputBorderHover};
  }
`

